import React from "react";
import { graphql } from "gatsby";

import { Container, Box, Typography } from "@material-ui/core";
import { Link } from "@runly/gatsby-theme";

import { ReadingThemeProvider } from "../theme";
import Layout from "../layout";
import Seo from "../seo";

import PlanSummary from "../pricing/summary";
import PlanComparison from "../pricing/comparison";
import PricingFaqs from "../pricing/faqs";

import CtaText from "../cta/text";
import CtaButton from "../cta/button";

const PricingPage = ({
	location,
	data: {
		allPlan: { plans },
		faqFiles: { faqs }
	}
}) => (
	<Layout location={location}>
		<Seo
			title="Pricing"
			description="Upgrade your jobs with a Runly subscription."
		/>

		<Container maxWidth="lg">
			<Box pb={2} component="header">
				<Typography variant="h2" gutterBottom>
					Upgrade your jobs with a subscription
				</Typography>

				<Typography paragraph>
					The Runly core packages are{" "}
					<strong>
						<Link
							href="https://github.com/runlyio/core-dotnet"
							color="inherit"
							underline="always"
						>
							open source and MIT licensed
						</Link>
					</strong>
					. Build, run, and distribute your application as you see fit.
				</Typography>
				<Typography paragraph>
					The subscriptions below allow you to take advantage of the{" "}
					<Link to="/platform/" color="inherit" underline="always">
						Runly Platform
					</Link>{" "}
					to shepherd your jobs from deployment to monitoring and scaling in
					production.
				</Typography>
			</Box>

			<PlanSummary plans={plans} component="section" />

			<Box pt={2} pb={6} component="section">
				<Typography variant="h4">Don't see what you're looking for?</Typography>
				<Typography paragraph>
					<Link href="mailto:sales@runly.io">Contact us</Link> and we will come
					up with a plan to meet the needs of <strong>your business</strong>.
				</Typography>
			</Box>

			<Box pt={8} pb={2} textAlign="center">
				<Typography variant="h3">Feature Comparison</Typography>
				<Typography variant="subtitle1" gutterBottom>
					From solo work to enterprise teams with advanced infrastructure
				</Typography>
			</Box>

			<PlanComparison plans={plans} component="section" />

			<Box pt={12} component="section">
				<ReadingThemeProvider>
					<Box clone textAlign="center">
						<Typography variant="h4" component="h2" gutterBottom>
							Frequently Asked Questions
						</Typography>
					</Box>

					<PricingFaqs faqs={faqs} />

					<Box clone textAlign="center">
						<Typography variant="h6">
							Still have questions?{" "}
							<Link href="mailto:sales@runly.io">Ask us</Link>.
						</Typography>
					</Box>
				</ReadingThemeProvider>
			</Box>

			<Box pt={6} textAlign="center">
				<CtaText title="Ready to try Runly?">
					<CtaButton href="/dashboard/">Get started for free</CtaButton>
				</CtaText>
			</Box>
		</Container>
	</Layout>
);

export default PricingPage;

export const pricingQuery = graphql`
	query PricingQuery {
		allPlan {
			plans: nodes {
				id: id__normalized
				name
				description

				environmentCount
				clusterCount
				nodeCount
				userCount
				templateCount
				allowApps

				features
				roles

				isFeatured

				price
			}
		}
		faqFiles: allFile(
			filter: { sourceInstanceName: { eq: "pricingFaq" } }
			sort: { fields: childMdx___frontmatter___sort }
		) {
			faqs: nodes {
				name
				childMdx {
					frontmatter {
						question
					}
					answer: body
				}
			}
		}
	}
`;
